<LayoutMain class="edit-personal">
  <PersonalsTopBar :showCreateCTA="false" />

  <PageHeader :title="t('personals.editAd')" :subtitle="t('personals.editSubtitle')">
    <template #actions>
      <button variant="ghost" size="sm" @click="handleCancel">
        <ArrowLeft :size="16" />
        {{ t('common.actions.cancel') }}
      </button>
    </template>
  </PageHeader>

  <div class="space-y-6">
    <!-- Status Messages -->
    <div v-if="submitMessage" :class="submitSuccess ? 'success-message' : 'error-message'">
      {{ submitMessage }}
    </div>

    <!-- Error Display -->
    <ErrorDisplay v-if="error" :error="error" @retry="loadAd" class="mb-6" />

    <!-- Loading State -->
    <div v-if="isLoading" class="flex justify-center py-12">
      <LoadingSpinner size="lg" />
    </div>

    <!-- Edit Form -->
    <Card variant="elevated" v-else-if="form.title || form.body">
      <template #header>
        <Heading level="2" class="text-xl font-semibold text-forest-primary">
          {{ t('personals.editDetails') }}
        </Heading>
      </template>

      <div class="space-y-6">
        <!-- Title Field -->
        <div>
          <label class="block text-sm font-medium text-forest-primary mb-2">
            {{ t('personals.formTitle') }}
            <span class="text-accent-coral">*</span>
          </label>

          <input
            v-model="form.title"
            :placeholder="t('personals.titlePlaceholder')"
            :error="errors.title"
            :disabled="form.isSubmitting"
            @input="validateForm"
            class="w-full"
          />

          <div class="flex items-center justify-between mt-2">
            <Text
              variant="caption"
              :class="{
                'character-counter': true,
                'warning': titleCharacterCount > 80,
                'danger': titleCharacterCount > 100
              }"
            >
              {{ titleCharacterCount }} / 100 {{ t('common.characters') }}
            </Text>

            <div class="text-red-500" v-if="errors.title">
              <Text variant="caption">{{ errors.title }}</Text>
            </div>
          </div>
        </div>

        <!-- Body Field -->
        <div>
          <label class="block text-sm font-medium text-forest-primary mb-2">
            {{ t('personals.description') }}
            <span class="text-accent-coral">*</span>
          </label>

          <textarea
            v-model="form.body"
            :placeholder="t('personals.descriptionPlaceholder')"
            :error="errors.body"
            :disabled="form.isSubmitting"
            @input="validateForm"
            rows="8"
            class="w-full"
          />

          <div class="flex items-center justify-between mt-2">
            <Text
              variant="caption"
              :class="{
                'character-counter': true,
                'warning': bodyCharacterCount > 1600,
                'danger': bodyCharacterCount > 2000
              }"
            >
              {{ bodyCharacterCount }} / 2000 {{ t('common.characters') }}
            </Text>

            <div class="text-red-500" v-if="errors.body">
              <Text variant="caption">{{ errors.body }}</Text>
            </div>
          </div>
        </div>

        <!-- Guidelines -->
        <div class="info-box">
          <Heading level="3" class="text-sm font-semibold mb-2">
            {{ t('personals.editGuidelines') }}
          </Heading>

          <ul class="text-sm space-y-1">
            <li>• {{ t('personals.editGuideline1') }}</li>
            <li>• {{ t('personals.editGuideline2') }}</li>
            <li>• {{ t('personals.editGuideline3') }}</li>
          </ul>
        </div>
      </div>
    </Card>

    <!-- Not Found State -->
    <Card variant="elevated" v-else-if="!isLoading">
      <div class="text-center py-6">
        <div class="text-neutral-400 mb-4">
          <AlertTriangle :size="48" class="mx-auto mb-3" />
        </div>

        <Heading level="3" class="text-lg font-semibold text-forest-primary mb-2">
          {{ t('personals.adNotFound') }}
        </Heading>

        <Text variant="body" class="text-neutral-600 mb-4">
          {{ t('personals.adNotFoundDescription') }}
        </Text>

        <button variant="primary" @click="goBack">{{ t('common.actions.goBack') }}</button>
      </div>
    </Card>
  </div>

  <!-- Form Actions (Sticky Bottom) -->
  <div v-if="form.title || form.body" class="form-actions">
    <div class="flex justify-end gap-3">
      <button variant="outline" @click="handleCancel" :disabled="form.isSubmitting">
        {{ t('common.actions.cancel') }}
      </button>

      <button
        variant="primary"
        @click="handleSubmit"
        :disabled="form.isSubmitting || !form.title.trim() || !form.body.trim()"
      >
        <LoadingSpinner v-if="form.isSubmitting" size="sm" class="mr-2" />
        <Save v-else :size="16" class="mr-2" />
        {{ form.isSubmitting ? t('common.actions.updating') : t('common.actions.update') }}
      </button>
    </div>
  </div>
</LayoutMain>
